import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import { toast } from "sonner";
import {
  GraduationCap, PlayCircle, Play, LockKey, Plus, PencilSimple, Trash, ArrowLeft, Crown,
  CheckCircle, Circle, CaretUp, CaretDown, CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const FALLBACK_THUMBS = [
  "https://images.unsplash.com/photo-1567095751004-aa51a2690368?crop=entropy&cs=srgb&fm=jpg&w=640&q=80",
  "https://images.unsplash.com/photo-1557264322-b44d383a2906?crop=entropy&cs=srgb&fm=jpg&w=640&q=80",
  "https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&w=640&q=80",
  "https://images.pexels.com/photos/13111752/pexels-photo-13111752.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=420&w=640",
];
const HERO_BG = "https://images.unsplash.com/photo-1565021324587-5fd009870e68?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";

const ytThumb = (embed) => {
  const m = embed && embed.match(/youtube\.com\/embed\/([A-Za-z0-9_-]+)/);
  return m ? `https://img.youtube.com/vi/${m[1]}/maxresdefault.jpg` : null;
};
const cardThumb = (v, i) => v.thumbnail || ytThumb(v.embed_url) || FALLBACK_THUMBS[i % FALLBACK_THUMBS.length];

const emptyForm = { id: null, title: "", description: "", url: "", tier: "basic", thumbnail: "" };

export default function TrainingCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPro, openUpgrade } = useSubscription();
  const isAdmin = !!user?.is_admin;

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/training");
      setVideos(res.data || []);
    } catch (e) {
      toast.error("Could not load training videos");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const basic = videos.filter((v) => v.tier === "basic");
  const expert = videos.filter((v) => v.tier === "expert");
  const pct = (items) => (!items.length ? 0 : Math.round((items.filter((v) => v.watched).length / items.length) * 100));
  const overallPct = pct(videos);
  const featured = videos.find((v) => !v.watched && !v.locked) || videos.find((v) => !v.locked) || videos[0];

  const setWatched = async (v, watched) => {
    setVideos((vs) => vs.map((x) => (x.id === v.id ? { ...x, watched } : x)));
    try { await api.post(`/training/${v.id}/watch`, { watched }); }
    catch (e) {
      setVideos((vs) => vs.map((x) => (x.id === v.id ? { ...x, watched: !watched } : x)));
      toast.error(e?.response?.data?.detail || "Could not update progress");
    }
  };

  const openVideo = (v) => {
    if (v.locked) { openUpgrade("Expert training is a Pro feature. Upgrade to unlock advanced FlyerStudio tutorials."); return; }
    setPlaying(v);
    if (!v.watched) setWatched(v, true);
  };

  const saveVideo = async () => {
    if (!form.title.trim() || !form.url.trim()) { toast.error("Title and video link are required"); return; }
    setSaving(true);
    try {
      const payload = { title: form.title.trim(), description: form.description.trim(), url: form.url.trim(), tier: form.tier, thumbnail: form.thumbnail.trim() || null };
      if (form.id) { await api.put(`/training/${form.id}`, payload); toast.success("Video updated"); }
      else { await api.post("/training", payload); toast.success("Video added"); }
      setForm(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save video"); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    const v = confirmDel; setConfirmDel(null);
    try { await api.delete(`/training/${v.id}`); setVideos((vs) => vs.filter((x) => x.id !== v.id)); toast.success("Video deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete video"); }
  };

  const move = async (v, dir) => {
    const list = v.tier === "basic" ? [...basic] : [...expert];
    const i = list.findIndex((x) => x.id === v.id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    const other = v.tier === "basic" ? expert : basic;
    const merged = v.tier === "basic" ? [...list, ...other] : [...other, ...list];
    setVideos(merged);
    try { await api.post("/training/reorder", { ids: merged.map((x) => x.id) }); }
    catch (e) { toast.error("Could not reorder"); load(); }
  };

  const VideoCard = ({ v, index, count }) => (
    <div className="group flex-none w-[260px] md:w-[310px] snap-start transition-all duration-300 hover:scale-[1.04] hover:z-20">
    <div
      data-testid={`training-card-${v.id}`}
      onClick={() => openVideo(v)}
      className={`relative aspect-video overflow-hidden cursor-pointer ring-1 ring-white/10 group-hover:ring-white/40 shadow-xl bg-zinc-900 ${isAdmin ? "rounded-t-xl" : "rounded-xl"}`}
    >
      <img src={cardThumb(v, index)} alt={v.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-110 group-hover:opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/20" />

      {/* center play on hover */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        {v.locked ? <LockKey size={50} weight="fill" className="text-amber-300 drop-shadow-lg" /> : <PlayCircle size={62} weight="fill" className="text-white drop-shadow-lg" />}
      </div>

      {/* sequence number */}
      <span className="absolute top-2.5 left-2.5 inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-md bg-black/60 backdrop-blur-md ring-1 ring-white/20 text-[11px] font-bold text-white z-10">#{index + 1}</span>

      {/* status badge top-right */}
      {v.locked ? (
        <span className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-xl text-amber-400 text-[10px] font-bold ring-1 ring-amber-500/50 shadow-[0_0_15px_rgba(245,158,11,0.25)] z-10">
          <Crown size={11} weight="fill" /> PRO
        </span>
      ) : v.watched ? (
        <span className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/90 text-white text-[10px] font-bold ring-1 ring-emerald-300/40 z-10">
          <CheckCircle size={11} weight="fill" /> DONE
        </span>
      ) : null}

      {/* bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 px-3 pt-8 pb-3 bg-gradient-to-t from-black/95 via-black/60 to-transparent z-10">
        <p className="text-sm font-semibold leading-tight text-white line-clamp-1" title={v.title}>{v.title}</p>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">{v.tier === "expert" ? "Expert" : "Basic"} • Lesson {index + 1}</span>
          {!v.locked && (
            <button
              data-testid={`watch-toggle-${v.id}`}
              onClick={(e) => { e.stopPropagation(); setWatched(v, !v.watched); }}
              title={v.watched ? "Mark as not watched" : "Mark as watched"}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold transition-colors ${v.watched ? "text-emerald-400 hover:text-emerald-300" : "text-zinc-300 hover:text-white"}`}
            >
              {v.watched ? <CheckCircle size={15} weight="fill" /> : <Circle size={15} />}
              {v.watched ? "Watched" : "Mark done"}
            </button>
          )}
        </div>
      </div>

      {/* per-card progress edge */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/15 z-20">
        <div className="h-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.6)] transition-all duration-500" style={{ width: v.watched ? "100%" : "0%" }} />
      </div>
    </div>

    {/* admin control bar — always visible, clearly labeled */}
    {isAdmin && (
      <div data-testid={`admin-bar-${v.id}`} className="flex items-center justify-between gap-1 px-2 py-2 bg-zinc-900 rounded-b-xl ring-1 ring-white/10 border-t border-white/5">
        <div className="flex items-center gap-1">
          <button data-testid={`move-up-${v.id}`} disabled={index === 0} onClick={(e) => { e.stopPropagation(); move(v, -1); }} title="Move earlier" className="h-8 w-8 flex items-center justify-center text-zinc-300 hover:text-white hover:bg-white/10 rounded-md transition-colors disabled:opacity-25"><CaretLeft size={16} weight="bold" /></button>
          <button data-testid={`move-down-${v.id}`} disabled={index === count - 1} onClick={(e) => { e.stopPropagation(); move(v, 1); }} title="Move later" className="h-8 w-8 flex items-center justify-center text-zinc-300 hover:text-white hover:bg-white/10 rounded-md transition-colors disabled:opacity-25"><CaretRight size={16} weight="bold" /></button>
        </div>
        <div className="flex items-center gap-1.5">
          <button data-testid={`edit-training-${v.id}`} onClick={(e) => { e.stopPropagation(); setForm({ id: v.id, title: v.title, description: v.description || "", url: v.url || "", tier: v.tier, thumbnail: v.thumbnail || "" }); }} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-zinc-200 bg-white/5 hover:bg-blue-600 hover:text-white transition-colors"><PencilSimple size={14} weight="bold" /> Edit</button>
          <button data-testid={`delete-training-${v.id}`} onClick={(e) => { e.stopPropagation(); setConfirmDel(v); }} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-zinc-200 bg-white/5 hover:bg-red-600 hover:text-white transition-colors"><Trash size={14} weight="bold" /> Delete</button>
        </div>
      </div>
    )}
    </div>
  );

  const Rail = ({ icon: Icon, title, subtitle, items, color, locked }) => {
    const ref = useRef(null);
    const scroll = (dir) => ref.current?.scrollBy({ left: dir * (ref.current.clientWidth * 0.8), behavior: "smooth" });
    const completed = items.filter((v) => v.watched).length;
    return (
      <section className="mb-10 tc-fade-up">
        <div className="flex items-end justify-between gap-4 px-6 md:px-12 mb-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${color}`}><Icon size={20} weight="fill" className="text-white" /></div>
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{title}</h2>
              <p className="text-xs text-zinc-500">{subtitle}</p>
            </div>
          </div>
          {items.length > 0 && !locked && (
            <div className="flex items-center gap-3 min-w-[180px]">
              <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct(items)}%` }} />
              </div>
              <span data-testid={`progress-${title.split(" ")[0].toLowerCase()}`} className="text-xs font-semibold text-zinc-400 whitespace-nowrap">{completed}/{items.length} done</span>
            </div>
          )}
        </div>

        {items.length === 0 ? (
          <div className="mx-6 md:mx-12 rounded-xl border border-dashed border-white/10 py-12 text-center text-zinc-500 text-sm">No videos here yet.</div>
        ) : (
          <div className="relative group/rail">
            <button onClick={() => scroll(-1)} className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-30 h-11 w-11 rounded-full bg-black/70 backdrop-blur-md ring-1 ring-white/10 items-center justify-center text-white opacity-0 group-hover/rail:opacity-100 hover:bg-black/90 transition-all"><CaretLeft size={20} weight="bold" /></button>
            <button onClick={() => scroll(1)} className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-30 h-11 w-11 rounded-full bg-black/70 backdrop-blur-md ring-1 ring-white/10 items-center justify-center text-white opacity-0 group-hover/rail:opacity-100 hover:bg-black/90 transition-all"><CaretRight size={20} weight="bold" /></button>
            <div ref={ref} className="flex overflow-x-auto gap-4 px-6 md:px-12 py-4 snap-x snap-mandatory scrollbar-hide rail-mask">
              {items.map((v, i) => <VideoCard key={v.id} v={v} index={i} count={items.length} />)}
            </div>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-body">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/50 border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} data-testid="training-back-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"><ArrowLeft size={18} /></button>
            <span className="font-display font-extrabold text-lg tracking-tight">FlyerStudio <span className="text-blue-500">Academy</span></span>
          </div>
          {isAdmin && (
            <button data-testid="add-training-btn" onClick={() => setForm(emptyForm)} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors"><Plus weight="bold" size={16} /> Add video</button>
          )}
        </div>
      </header>

      {loading ? (
        <div className="py-32 text-center text-zinc-500">Loading…</div>
      ) : videos.length === 0 ? (
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 text-center">
          <GraduationCap size={56} weight="duotone" className="mx-auto text-blue-500/60 mb-4" />
          <h1 className="font-display text-3xl font-bold mb-2">The Academy is coming soon 🎬</h1>
          <p className="text-zinc-400">{isAdmin ? "Add your first tutorial with the “Add video” button above." : "Training videos will appear here shortly."}</p>
        </div>
      ) : (
        <>
          {/* Cinematic hero */}
          <section className="relative w-full h-[58vh] min-h-[380px] max-h-[600px] flex items-end overflow-hidden tc-fade-up">
            <img src={featured ? cardThumb(featured, 0) : HERO_BG} alt="featured" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.src = HERO_BG; }} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/55 to-black/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/85 via-[#050505]/30 to-transparent" />
            <div className="relative z-20 max-w-3xl px-6 md:px-12 pb-12 md:pb-16 flex flex-col gap-4">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-400"><GraduationCap weight="fill" size={15} /> Featured Tutorial</span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter drop-shadow-2xl leading-[0.95]">{featured?.title || "Master FlyerStudio"}</h1>
              {featured?.description && <p className="text-zinc-300 text-base md:text-lg max-w-xl line-clamp-2 drop-shadow">{featured.description}</p>}
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <button data-testid="hero-play-btn" onClick={() => featured && openVideo(featured)} className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold px-7 py-3 rounded-md transition-colors"><Play weight="fill" size={18} /> Play</button>
                <div className="flex items-center gap-3 px-4 py-2.5 rounded-md bg-white/10 backdrop-blur-md ring-1 ring-white/10">
                  <div className="w-28 sm:w-40 h-2 rounded-full bg-white/20 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-700" style={{ width: `${overallPct}%` }} />
                  </div>
                  <span data-testid="overall-progress" className="text-sm font-bold whitespace-nowrap">{overallPct}% complete</span>
                </div>
              </div>
            </div>
          </section>

          <main className="max-w-[1600px] mx-auto py-8 -mt-8 relative z-30">
            <Rail icon={GraduationCap} title="Basic Training" subtitle="Free for everyone — the essentials." items={basic} color="bg-blue-600" />
            <Rail icon={Crown} title="Expert Training" subtitle={isPro || isAdmin ? "Advanced pro techniques." : "Pro members only — upgrade to unlock."} items={expert} color="bg-amber-500" locked={!isPro && !isAdmin} />

            {!isPro && !isAdmin && expert.length > 0 && (
              <div className="relative overflow-hidden rounded-2xl p-8 ring-1 ring-white/10 flex flex-col md:flex-row items-center justify-between gap-6 my-10 mx-6 md:mx-12 bg-gradient-to-br from-zinc-900 to-black">
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 to-fuchsia-500/20 blur-2xl z-0" />
                <div className="relative z-10">
                  <p className="font-display text-xl font-bold flex items-center gap-2"><Crown weight="fill" size={20} className="text-amber-300" /> Unlock Expert Training</p>
                  <p className="text-sm text-zinc-300 mt-1.5 max-w-lg">Go Pro to stream every advanced tutorial and master client-ready flyers like a studio.</p>
                </div>
                <button data-testid="training-upgrade-btn" onClick={() => openUpgrade("Upgrade to unlock Expert Training videos.")} className="relative z-10 inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-400 text-black font-bold px-7 py-3 rounded-full hover:scale-105 transition-transform"><Crown weight="fill" size={16} /> Upgrade to Pro</button>
              </div>
            )}
          </main>
        </>
      )}

      {/* Player modal */}
      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="bg-[#0d0d0d] border-white/10 text-white sm:max-w-3xl p-0 overflow-hidden" data-testid="training-player">
          <DialogTitle className="sr-only">{playing?.title || "Training video"}</DialogTitle>
          <DialogDescription className="sr-only">{playing?.description || "FlyerStudio training video player"}</DialogDescription>
          <div className="aspect-video w-full bg-black">
            {playing && (playing.provider === "file" ? (
              <video src={playing.url} controls autoPlay className="w-full h-full" />
            ) : (
              <iframe src={playing.embed_url} title={playing.title} className="w-full h-full" frameBorder="0" allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowFullScreen />
            ))}
          </div>
          <div className="p-5">
            <h3 className="font-display text-lg font-bold">{playing?.title}</h3>
            {playing?.description && <p className="text-sm text-zinc-400 mt-1">{playing.description}</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add / Edit modal */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-[#141414] border-white/10 text-white sm:max-w-lg" data-testid="training-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{form?.id ? "Edit video" : "Add training video"}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">Paste a YouTube, Vimeo, Google Drive, or direct video link and choose a tier.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4 py-1">
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2 block">Title</label>
                <input data-testid="training-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full h-10 rounded-lg bg-black/40 border border-white/10 px-3 text-sm outline-none focus:border-blue-500/60" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2 block">Video link (YouTube, Vimeo, Google Drive, or any URL)</label>
                <input data-testid="training-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://youtube.com/watch?v=…" className="w-full h-10 rounded-lg bg-black/40 border border-white/10 px-3 text-sm outline-none focus:border-blue-500/60" />
                <p className="text-[11px] text-zinc-500 mt-1.5">Tip: For Google Drive, set the file's sharing to "Anyone with the link" and paste the share URL.</p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2 block">Description (optional)</label>
                <textarea data-testid="training-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm outline-none focus:border-blue-500/60 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2 block">Tier</label>
                  <select data-testid="training-tier-select" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="w-full h-10 rounded-lg bg-black/40 border border-white/10 px-3 text-sm outline-none focus:border-blue-500/60">
                    <option value="basic">Basic (Free)</option>
                    <option value="expert">Expert (Pro only)</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2 block">Thumbnail URL (optional)</label>
                  <input data-testid="training-thumb-input" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} placeholder="https://…" className="w-full h-10 rounded-lg bg-black/40 border border-white/10 px-3 text-sm outline-none focus:border-blue-500/60" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg text-sm font-semibold text-zinc-300 hover:bg-white/5 transition-colors">Cancel</button>
            <button data-testid="save-training-btn" onClick={saveVideo} disabled={saving} className="h-10 px-5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors disabled:opacity-60">{saving ? "Saving…" : (form?.id ? "Save changes" : "Add video")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="bg-[#141414] border-white/10 text-white sm:max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Delete video?</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">“{confirmDel?.title}” will be permanently removed from the Training Center.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setConfirmDel(null)} className="h-10 px-4 rounded-lg text-sm font-semibold text-zinc-300 hover:bg-white/5 transition-colors">Cancel</button>
            <button data-testid="confirm-delete-btn" onClick={doDelete} className="h-10 px-5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold transition-colors">Delete</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
